import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { integrationsApi, type ConnectInput, type UpdateInput, type RotateInput } from '../api/integrations.js';
import { queryKeys } from '../lib/queryKeys.js';

export function useIntegrations() {
  return useQuery({
    queryKey: queryKeys.integrations.list(),
    queryFn: () => integrationsApi.list(),
    select: (data) => data.items,
  });
}

export function useIntegration(id: string) {
  return useQuery({
    queryKey: queryKeys.integrations.detail(id),
    queryFn: () => integrationsApi.get(id),
    enabled: !!id,
  });
}

export function useConnectIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ConnectInput) => integrationsApi.connect(input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.integrations.all });
      toast.success(`${data.displayName} connected successfully`);
    },
    onError: (err: Error) => {
      toast.error(`Connection failed: ${err.message}`);
    },
  });
}

export function useUpdateIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateInput }) =>
      integrationsApi.update(id, input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.integrations.all });
      toast.success(`${data.displayName} updated`);
    },
    onError: (err: Error) => {
      toast.error(`Update failed: ${err.message}`);
    },
  });
}

export function useTestIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => integrationsApi.test(id),
    onSuccess: (result, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.integrations.list() });
      if (result.ok) {
        toast.success(`Test passed — ${result.message}`, {
          description: result.detail,
        });
      } else {
        toast.error(`Test failed — ${result.message}`, {
          description: result.detail,
        });
      }
    },
    onError: (err: Error) => {
      toast.error(`Test error: ${err.message}`);
    },
  });
}

export function useRotateSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RotateInput }) =>
      integrationsApi.rotate(id, input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.integrations.all });
      toast.success(`Secret rotated for ${data.displayName}`);
    },
    onError: (err: Error) => {
      toast.error(`Rotation failed: ${err.message}`);
    },
  });
}

export function useDisconnectIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => integrationsApi.disconnect(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.integrations.all });
      toast.success('Integration disconnected');
    },
    onError: (err: Error) => {
      toast.error(`Disconnect failed: ${err.message}`);
    },
  });
}
